import { databases } from '../lib/appwrite';
import { ID, Query, Permission, Role } from 'appwrite';

// ============================================
// APPWRITE PROFILE SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const PROFILE_COLLECTION_ID = process.env.REACT_APP_APPWRITE_PROFILE_COLLECTION_ID;

// Cache to prevent duplicate profile creation during race conditions
const profileCreationCache = new Map();

// Debug: Log environment variables on load
console.log('Profile Service Config:', {
  DATABASE_ID,
  PROFILE_COLLECTION_ID
});

/**
 * Create a new profile for a user
 * @param {string} userId - Appwrite User ID
 * @param {string} fullName - User's full name
 * @param {string} email - User's email
 * @param {string} role - User's role (customer or vendor), default: customer
 * @returns {Promise<Object>} - Created profile document
 */
export const createProfile = async (userId, fullName, email, role = 'customer') => {
  try {
    console.log('Creating profile for user:', { userId, fullName, email, role });
    console.log('Using DATABASE_ID:', DATABASE_ID);
    console.log('Using PROFILE_COLLECTION_ID:', PROFILE_COLLECTION_ID);
    
    const profile = await databases.createDocument(
      DATABASE_ID,
      PROFILE_COLLECTION_ID,
      ID.unique(),
      {
        user_id: userId,
        full_name: fullName,
        email: email,
        role: role,
        wallet_balance: 0,
        loyalty_points: 0
      },
      [
        Permission.read(Role.user(userId)),
        Permission.update(Role.user(userId)),
        Permission.delete(Role.user(userId))
      ]
    );
    console.log('Profile created successfully:', profile);
    return profile;
  } catch (error) {
    console.error('Create profile error:', error);
    console.error('Error details:', error.message, error.code, error.type);
    throw error;
  }
};

/**
 * Get a user's profile by user ID
 * @param {string} userId - Appwrite User ID
 * @returns {Promise<Object|null>} - Profile document or null if not found
 */
export const getProfileByUserId = async (userId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      PROFILE_COLLECTION_ID,
      [Query.equal('user_id', userId)]
    );
    
    if (response.documents.length > 0) {
      return response.documents[0];
    }
    return null;
  } catch (error) {
    console.error('Get profile error:', error);
    throw error;
  }
};

/**
 * Update a user's profile
 * @param {string} documentId - Profile document ID
 * @param {Object} data - Data to update
 * @returns {Promise<Object>} - Updated profile document
 */
export const updateProfile = async (documentId, data) => {
  try {
    const profile = await databases.updateDocument(
      DATABASE_ID,
      PROFILE_COLLECTION_ID,
      documentId,
      data
    );
    return profile;
  } catch (error) {
    console.error('Update profile error:', error);
    throw error;
  }
};

/**
 * Update user's wallet balance
 * @param {string} documentId - Profile document ID
 * @param {number} newBalance - New wallet balance
 * @returns {Promise<Object>} - Updated profile document
 */
export const updateWalletBalance = async (documentId, newBalance) => {
  return updateProfile(documentId, { wallet_balance: newBalance });
};

/**
 * Update user's loyalty points
 * @param {string} documentId - Profile document ID
 * @param {number} newPoints - New loyalty points value
 * @returns {Promise<Object>} - Updated profile document
 */
export const updateLoyaltyPoints = async (documentId, newPoints) => {
  return updateProfile(documentId, { loyalty_points: newPoints });
};

/**
 * Update user's role
 * @param {string} documentId - Profile document ID
 * @param {string} newRole - New role (customer or vendor)
 * @returns {Promise<Object>} - Updated profile document
 */
export const updateRole = async (documentId, newRole) => {
  return updateProfile(documentId, { role: newRole });
};

/**
 * Get or create profile for a user
 * Useful for OAuth logins where profile might not exist
 * Includes safeguards against race conditions
 * @param {string} userId - Appwrite User ID
 * @param {string} fullName - User's full name
 * @param {string} email - User's email
 * @returns {Promise<Object>} - Profile document
 */
export const getOrCreateProfile = async (userId, fullName, email) => {
  // Check if we're already creating a profile for this user (before any async work)
  if (profileCreationCache.has(userId)) {
    console.log('Profile creation/fetch already in progress for user:', userId, '- waiting for existing promise');
    return await profileCreationCache.get(userId);
  }
  
  // Create the work promise and cache it immediately
  const workPromise = (async () => {
    try {
      // Try to get existing profile
      console.log('Checking for existing profile for user:', userId);
      let profile = await getProfileByUserId(userId);
      
      // If no profile exists, create one
      if (!profile) {
        console.log('No profile found, creating new profile for user:', userId);
        
        try {
          profile = await createProfile(userId, fullName, email);
          console.log('Profile created successfully:', profile);
        } catch (createError) {
          // If error is 409 (conflict/duplicate), try to fetch the profile again
          if (createError.code === 409) {
            console.log('Profile already exists (conflict), fetching it...');
            profile = await getProfileByUserId(userId);
          } else {
            throw createError;
          }
        }
      } else {
        console.log('Existing profile found:', profile);

        // If email was provided (e.g., from OAuth) and differs from stored value, keep the profile in sync.
        if (email && profile.email !== email) {
          try {
            console.log('Updating profile email for user:', userId, { old: profile.email, new: email });
            profile = await updateProfile(profile.$id, { email });
          } catch (updateErr) {
            console.error('Failed to update profile email:', updateErr);
          }
        }
      }
      
      return profile;
    } finally {
      // Remove from cache after completion (success or failure)
      profileCreationCache.delete(userId);
    }
  })();
  
  // Store the promise immediately before any await
  profileCreationCache.set(userId, workPromise);
  
  try {
    return await workPromise;
  } catch (error) {
    console.error('Get or create profile error:', error);
    throw error;
  }
};

// ============================================
// DATABASE SCHEMA REFERENCE
// ============================================
// 
// Profiles Collection Schema:
// Collection ID: profile
// 
// Attributes:
// - user_id (String): Foreign key to the Appwrite System User ID
// - full_name (String): The user's name as seen in the profile dropdown
// - role (Enum): Values: customer, vendor (determines if they see "Sell Food" or "Buy Food" buttons)
//   Default: customer
// - wallet_balance (Float): Stores the RM balance (e.g., RM 45.50)
//   Initial: 0
// - loyalty_points (Integer): Stores available points (e.g., 1,250 points)
//   Initial: 0
// - email (String): The email address used to register the account
//   Note: May be absent for some OAuth providers; stored to keep contact and verification details
// - condo_id (String): Foreign key to the Condominiums collection (user's residence)
//   Note: This field should be added to link users to their condominium
// 
